import React, {useEffect, useState} from 'react';
import {Avatar, Badge, Box, Button, Flex, Heading, SimpleGrid, Spinner, Text, Image} from '@chakra-ui/react';
import {Link as RouterLink, useNavigate, useParams} from 'react-router-dom';
import axios from 'axios';
import {DeleteAccountButton} from '../../components/MailinatorCleanup';
import {ProviderApprovalSummary} from './verified';
import EditProfileDetails from '../../components/EditProfileDetails';
import ConvertToProvider from '../../components/ConvertToProvider';
import {regionSelections, regionForArea} from '../../utils/lagosRegions';

export function serviceNames(services: any): string[] {
  if (!Array.isArray(services)) return [];
  return Array.from(new Set(services.map(service => typeof service?.name === 'string' ? service.name.trim() : '').filter(Boolean))) as string[];
}
const shown = (value: any) => typeof value === 'string' && value.trim() ? value : 'Not provided';
const photoUrl = (value: any) => typeof value === 'string' && /^https?:\/\//i.test(value) ? value : undefined;
const registrationDate = (value: unknown) => {
  if (typeof value !== 'string' || !value.trim()) return 'Not available';
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return 'Not available';
  return new Intl.DateTimeFormat('en-GB', {timeZone: 'Africa/Lagos', day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true}).format(date);
};

export default function ProviderProfile({customer=false}:{customer?:boolean}) {
  const directory = customer ? 'customers' : 'providers';
  const {id} = useParams();
  const navigate = useNavigate();
  const [provider, setProvider] = useState<any>(null);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    setBusy(true); setError(''); setProvider(null);
    axios.get(`${process.env.REACT_APP_BASE_URL || 'https://api.pureworker.com/api'}/admin/${directory}`, {
      params: {_id: id, limit: 1}, signal: controller.signal, withCredentials: true, timeout: 25000,
    }).then(response => {
      if (controller.signal.aborted) return;
      const row = response.data?.[directory]?.results?.find((item: any) => String(item._id || item.id) === id);
      if (!row) {setError(`This ${customer?'customer':'provider'} was not found or has been deleted.`); return;}
      setProvider(row);
    }).catch(() => {if (!controller.signal.aborted) setError('Could not load this profile. Please retry.');})
      .finally(() => {if (!controller.signal.aborted) setBusy(false);});
    return () => controller.abort();
  }, [id, retry, directory, customer]);

  const name = provider?.businessName || [provider?.firstName, provider?.lastName].filter(Boolean).join(' ') || (customer ? 'Customer' : 'Service provider');
  const services = serviceNames(provider?.services);
  return <Box p={{base: 3, md: 6}} minW={0}>
    <Button as={RouterLink} to={customer?"/customers":"/serviceProviders"} variant="link" mb={5}>Back to {customer?"customers":"service providers"}</Button>
    {busy ? <Spinner aria-label="Loading provider profile" /> : error ? <Box role="alert"><Text mb={3}>{error}</Text><Button onClick={() => setRetry(value => value + 1)}>Retry</Button></Box> : provider && <>
      <Flex gap={4} align="center" wrap="wrap" mb={5}>
        <Avatar name={name} src={photoUrl(provider.profilePic)} size="lg" />
        <Box flex="1" minW="180px"><Heading size="lg">{name}</Heading><Text color="admin.muted">{customer?"Customer":"Service provider"} profile</Text></Box>
        <EditProfileDetails key={id} id={id || ''} onSaved={()=>setRetry(value=>value+1)} />
        {customer && provider.accountType==='customer' && <ConvertToProvider key={`convert-${id}`} id={id || ''} name={name}/>}
        <Button as={RouterLink} to={`/${customer?"customers":"provider"}/${encodeURIComponent(id || '')}/orders`} variant="outline">View orders</Button>
        <DeleteAccountButton account={provider} onRemoved={() => navigate(customer?'/customers':'/serviceProviders')} />
      </Flex>
      <Flex gap={2} wrap="wrap" mb={5}>
        <Badge>{provider.supended || provider.suspended ? 'Suspended' : provider.active === false ? 'Inactive' : 'Active'}</Badge>
      </Flex>
      {!customer && <ProviderApprovalSummary id={id || ''} />}
      <SimpleGrid columns={{base: 1, md: 2}} spacing={4} mb={6}>
        {[
          ['Name', [provider.firstName, provider.lastName].filter(Boolean).join(' ')], ['Business name', provider.businessName],
          ['Email', provider.email], ['Phone number', provider.phoneNumber], ['State', provider.state], ['Address', provider.address],
          ['Account type', provider.accountType], ['Working regions', regionSelections(provider.declaredAreas).map(area => regionForArea(area)?.name || area.replace(/-/g,' ')).join(', ')],
          ['Registered on (Lagos time)', registrationDate(provider.createdAt)],
        ].filter(([label])=>!customer || label!=='Working regions').map(([label, value]) => <Box key={label} p={4} bg="admin.surface" borderWidth="1px" borderRadius="md"><Text fontSize="sm" color="admin.muted">{label}</Text><Text overflowWrap="anywhere">{shown(value)}</Text></Box>)}
      </SimpleGrid>
      <Box mb={6}><Heading size="md" mb={2}>About this {customer?"customer":"provider"}</Heading><Text whiteSpace="pre-wrap">{shown(provider.description)}</Text></Box>
      {!customer && <Box id="services" mb={6}><Heading size="md" mb={3}>Services</Heading>
        {services.length ? <Flex gap={2} wrap="wrap">{services.map(service => <Badge key={service} p={2} colorScheme="purple" whiteSpace="normal">{service}</Badge>)}</Flex>
          : <Text color="admin.muted">{provider.services?.length ? 'Service details unavailable' : 'No services added'}</Text>}
      </Box>}
      {Array.isArray(provider.workPhotos) && !!provider.workPhotos.length && <Box><Heading size="md" mb={3}>Work photos</Heading><SimpleGrid columns={{base: 2, md: 4}} gap={3}>
        {provider.workPhotos.filter(photoUrl).map((url: string, index: number) => <Image key={`${index}-${url}`} src={url} alt={`${name} work photo ${index + 1}`} loading="lazy" objectFit="cover" borderRadius="md" maxH="220px" />)}
      </SimpleGrid></Box>}
    </>}
  </Box>;
}
